import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { parseError } from '../shared/error';
import { internalLayersKeys } from './queryKeys';
import type { LayerGrant } from 'hooks/internalLayer/types';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';

export const layerGrantKeys = {
  list: (internalLayerId: string) =>
    [...internalLayersKeys.all(), 'grants', internalLayerId] as const,
};

export function useLayerGrantsQuery(internalLayerId: string, enabled: boolean) {
  return useQuery({
    queryKey: layerGrantKeys.list(internalLayerId),
    enabled,
    queryFn: async () => {
      const resp = await axios.get<LayerGrant[]>(
        `${config.apiGateway.internal_layers}/${internalLayerId}/grants`
      );
      return resp.data;
    },
  });
}

export function useReplaceLayerGrantsMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    LayerGrant[],
    unknown,
    { internalLayerId: string; grants: LayerGrant[]; quiet?: boolean }
  >({
    mutationFn: async ({ internalLayerId, grants }) => {
      const resp = await axios.put<LayerGrant[]>(
        `${config.apiGateway.internal_layers}/${internalLayerId}/grants`,
        { grants }
      );
      return resp.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.quiet) return;
      toast.success('Draft sharing updated.');
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: layerGrantKeys.list(variables.internalLayerId),
      });
      queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() });
      queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() });
    },
  });
}
